import { useEffect, useState, useMemo, useCallback } from "react";
import { useShallow } from "zustand/shallow";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { Box, Divider, ListItemAvatar, ListItemButton, ListItemText, Typography } from "@mui/material";
import { mdiAccountPlusOutline } from "@mdi/js";

import ActionsDialog from "components/ActionsDialog";
import AppBar from "components/AppBar";
import AsyncAvatar from "components/AsyncAvatar";
import FAB from "components/FAB";
import AnimatedPlaceholder from "components/AnimatedPlaceholder";
import SearchInput from "components/SearchInput";
import PageLayout from "components/PageLayout";
import FriendRequest from "components/FriendRequest";
import useNotifier from "hooks/useNotifier";
import getNameInitials from "utils/getNameInitials";
import useBoundStore from "store";
import FriendApi, { FriendData, FriendRequest as FriendRequestI } from "api/friend";
import { StorageBuckets } from "api/storage";
import lottiePlaceholders from "assets/placeholders";
import compareString from "utils/compareString";
import VirtualizedList from "components/VirtualizedList";

export interface FriendsProps {
  onAddFriends: () => void;
  onBack?: () => void;
}

function Friends(props: FriendsProps) {
  const { onAddFriends, onBack } = props;
  const [openActions, setOpenActions] = useState<boolean>(false);
  const [filteredFriends, setFilteredFriends] = useState<FriendData[]>([]);
  const [searchString, setSearchString] = useState("");
  const [selectedFriend, setSelectedFriend] = useState<FriendData | null>(null);
  const userAuth = useBoundStore((state) => state.user.auth);
  const smallAvatars = useBoundStore((state) => state.miscellaneous.smallAvatars);
  const { friends, friendRequests } = useBoundStore(
    useShallow((state) => ({
      friends: state.appData.friends.sort((a, b) => compareString(a.name ?? "", b.name ?? "")),
      friendRequests: state.appData.friendRequests,
    }))
  );
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { showNotification } = useNotifier();
  const friendApi = useMemo(() => new FriendApi(showNotification), [showNotification]);
  const sentRequests: FriendRequestI[] = [];
  const receivedRequests: FriendRequestI[] = [];

  friendRequests?.forEach((friend: FriendRequestI) =>
    friend.sent_by_uid === userAuth!.id ? sentRequests.push(friend) : receivedRequests.push(friend)
  );

  /**
   * Handle search friend input
   *
   * @param newSearchString
   * @param newFilteredFriends
   */
  function handleSearchedFriend(newSearchString: string, newFilteredFriends: FriendData[]): void {
    setSearchString(newSearchString);
    setFilteredFriends(newFilteredFriends);
  }

  /**
   * Handle select friend
   *
   * @param friend
   */
  function handleSelectFriend(friend: FriendData): void {
    setSelectedFriend(friend);
    handleOpenActions();
  }

  /**
   * Cancel sent friend request
   *
   * @param requestId
   */
  async function handleCancelFriendRequest(requestId: number): Promise<void> {
    await friendApi.deleteFriendOrRequest(requestId);
  }

  /**
   * Accept received friend request
   *
   * @param request
   */
  async function handleAcceptFriendRequest(request: FriendRequestI): Promise<void> {
    await friendApi.acceptFriendRequest(request.id);
  }

  /**
   * Reject received friend request
   *
   * @param requestId
   */
  async function handleRejectFriendRequest(requestId: number): Promise<void> {
    await friendApi.deleteFriendOrRequest(requestId);
  }

  /**
   * Remove friend from friends list
   *
   * @param friendUid
   */
  async function handleRemoveFriend(friendUid: number) {
    await friendApi.deleteFriendOrRequest(friendUid);
  }

  /**
   * Handle open friend actions
   */
  const handleOpenActions = useCallback(() => {
    setOpenActions(true);
    navigate(location.pathname);
  }, []);

  /**
   * Handle close friend actions
   */
  function handleCloseDialog(): void {
    setOpenActions(false);
    navigate(-1);
  }

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setOpenActions(false);
    }
  }, [navigationType]);

  function getFriendsToRender() {
    if (searchString === "") {
      return friends.length > 0 ? friends : [];
    }

    return filteredFriends;
  }

  /**
   * Render received friends requests
   *
   * @param friendRequest
   * @returns
   */
  function renderReceivedRequest(friendRequest: FriendRequestI): JSX.Element {
    return (
      <FriendRequest
        avatar={
          <AsyncAvatar
            avatarFile={friendRequest.avatar ? smallAvatars[friendRequest.avatar] : undefined}
            avatarName={friendRequest.avatar}
            placeholder={getNameInitials(friendRequest.name)}
            bucket={StorageBuckets.PROFILES_AVATARS}
          />
        }
        name={friendRequest.name ?? "-"}
        key={friendRequest.id}
        type='received'
        onAccept={() => handleAcceptFriendRequest(friendRequest)}
        onCancel={() => handleRejectFriendRequest(friendRequest.id)}
      />
    );
  }

  /**
   * Render sent friend requests
   *
   * @param friendRequest
   * @returns
   */
  function renderSentRequest(friendRequest: FriendRequestI): JSX.Element {
    return (
      <FriendRequest
        avatar={
          <AsyncAvatar
            avatarFile={friendRequest.avatar ? smallAvatars[friendRequest.avatar] : undefined}
            avatarName={friendRequest.avatar}
            placeholder={getNameInitials(friendRequest.name)}
            bucket={StorageBuckets.PROFILES_AVATARS}
          />
        }
        name={friendRequest.name ?? "-"}
        key={friendRequest.id}
        type='sent'
        onCancel={() => handleCancelFriendRequest(friendRequest.id)}
      />
    );
  }

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];
    vrList.push(
      <Box key='vr-item-1' px={2} py={0.75}>
        {receivedRequests.length > 0 ? receivedRequests.map((request) => renderReceivedRequest(request)) : null}

        {sentRequests.length > 0 ? sentRequests.map((request) => renderSentRequest(request)) : null}
      </Box>
    );
    if (friends.length) {
      vrList.push(
        <Box pl={2} pr={2} pt={2}>
          <Typography color='textSecondary' variant='body2'>
            {friends.length} {friends.length === 1 ? "friend" : "friends"}
          </Typography>
        </Box>
      );
      vrList.push(
        <SearchInput
          data={friends}
          onSearchedResults={handleSearchedFriend}
          inputProps={{
            "aria-label": "Search friends",
            placeholder: "Search friends",
          }}
          searchBy='name'
          searchString={searchString}
        />
      );
      vrList.push(<Divider />);
    }
    if (filteredFriends.length === 0 && searchString !== "") {
      vrList.push(
        <Box p={2}>
          <Typography align='center' color='textSecondary'>
            No friends were found with that name
          </Typography>
        </Box>
      );
    } else {
      const friendsToRender = getFriendsToRender();

      friendsToRender.forEach((friend) => {
        vrList.push(
          <ListItemButton key={friend.uid} onClick={() => handleSelectFriend(friend)}>
            <ListItemAvatar>
              <AsyncAvatar
                avatarFile={friend.avatar ? smallAvatars[friend.avatar] : undefined}
                avatarName={friend.avatar}
                placeholder={getNameInitials(friend.name)}
                bucket={StorageBuckets.PROFILES_AVATARS}
              />
            </ListItemAvatar>
            <ListItemText primary={friend.name} secondary={friend.tag} />
          </ListItemButton>
        );
      });
    }

    return vrList;
  }

  return (
    <PageLayout
      appBar={<AppBar title='Friends' onBack={onBack} />}
      fab={<FAB aria-label='add-group' onClick={onAddFriends} icon={mdiAccountPlusOutline} title='Plus' />}
      paddingBottom={false}
    >
      <>
        <VirtualizedList estimateSize={64} rows={generateVirtualizedList()} />

        <ActionsDialog
          open={openActions}
          onClose={handleCloseDialog}
          actions={[
            {
              label: "Remove friend",
              handler: () => handleRemoveFriend(selectedFriend!.id),
            },
          ]}
        />
      </>

      {/* Display placeholder if no friends or requests are present */}
      {friends.length === 0 && receivedRequests.length === 0 && sentRequests.length === 0 && searchString === "" ? (
        <AnimatedPlaceholder
          animationData={lottiePlaceholders.friends}
          text="Add a new friend by pressing the '+' button."
          width='100%'
        />
      ) : null}
    </PageLayout>
  );
}

export default Friends;
